import { Button } from "@dropbox/dig-components/buttons";
import { Chip } from "@dropbox/dig-components/chip";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  FileTemplateLine,
  LockLine,
  SearchLine,
  TeamLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_CreateTemplateButton } from "@mirage/analytics/events/types/click_create_template_button";
import { PAP_Click_TemplateListItem } from "@mirage/analytics/events/types/click_template_list_item";
import { DeleteConfirmationModal } from "@mirage/mosaics/ComposeAssistant/components/settings/DeleteConfirmationModal";
import { SourcesFromSettingsConversationPane } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsConversationPane";
import { SourcesFromSettingsModalStepper } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsModalStepper";
import { SourcesFromSettingsOne } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsOne";
import { SourcesFromSettingsSharingToggle } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsSharingToggle";
import { SourcesFromSettingsTemplatePreview } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsTemplatePreview";
import { SourcesFromSettingsThree } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsThree";
import { SourcesFromSettingsTwo } from "@mirage/mosaics/ComposeAssistant/components/settings/SourcesFromSettingsTwo";
import { getBackgroundForTemplate } from "@mirage/mosaics/ComposeAssistant/components/settings/TemplateSettingsModal";
import { useTemplateSettings } from "@mirage/mosaics/ComposeAssistant/components/settings/useTemplateSettings";
import type {
  AssistCustomTemplate,
  AssistTemplate,
} from "@mirage/shared/compose/assist-template";
import i18n from "@mirage/translations";
import isEqual from "lodash/isEqual";
import { memo, useCallback, useEffect, useState } from "react";
import { useTemplateCategories } from "./hooks/useTemplateCategories";
import {
  TEMPLATE_FILTERS,
  useTemplateFiltering,
  type TemplateFilterType,
} from "./hooks/useTemplateFiltering";
import { TemplateCard } from "./TemplateCard";
import { TemplateDetailView } from "./TemplateDetailView";
import styles from "./TemplatesTab.module.css";

interface TemplatesTabProps {
  templates: AssistCustomTemplate[];
  onUseTemplate: (templateID: string, templateName: string) => void;
  currentTemplateID?: string | null;
  saveTemplate: (
    template: AssistCustomTemplate,
    onSaved?: (savedTemplate: AssistCustomTemplate) => void,
  ) => Promise<void>;
  onDeleteTemplate?: (templateId: string) => void;
  setCreatedTemplateSnackbarMsg?: (msg: string | undefined) => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  showTemplateTypesFilter?: boolean;
}

export const TemplatesTab = memo(
  ({
    templates,
    onUseTemplate,
    currentTemplateID,
    saveTemplate,
    onDeleteTemplate,
    setCreatedTemplateSnackbarMsg,
    logComposeEvent,
    showTemplateTypesFilter = false,
  }: TemplatesTabProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedFilters, setSelectedFilters] = useState<
      TemplateFilterType[]
    >([]);
    const [selectedTemplate, setSelectedTemplate] =
      useState<AssistTemplate | null>(null);
    const [customTemplateModalState, setCustomTemplateModalState] = useState<{
      isOpen: boolean;
      templateID?: string;
    }>({ isOpen: false });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { allTemplates } = useTemplateCategories({ templates });
    const { filteredTemplates } = useTemplateFiltering({
      templates: allTemplates,
      searchQuery,
      selectedFilters,
    });
    const {
      currentTemplateState,
      setCurrentTemplateState,
      sourcesContents,
      handleAddSource,
      handleRemoveSource,
      handleModifyTemplatePostUserMessage,
      handleClickSave,
      handleChangeTemplateNameInput,
      handleChangeTemplateDescriptionInput,
      handleAccessTypeChange,
      loading,
      resetTemplateState,
    } = useTemplateSettings({
      templates: templates,
      saveTemplate,
      logComposeEvent: logComposeEvent,
      onDone: (_templateID: string) => {
        // Close the custom template modal stepper but keep the main modal open
        setCustomTemplateModalState({ isOpen: false });
        // Reset to show the template grid view
        setSelectedTemplate(null);
      },
      setCreatedTemplateSnackbarMsg:
        setCreatedTemplateSnackbarMsg || (() => {}),
    });

    const handleTemplateClick = useCallback(
      (template: AssistTemplate) => {
        logComposeEvent(PAP_Click_TemplateListItem());
        setSelectedTemplate(template);
      },
      [logComposeEvent],
    );

    const handleUseTemplate = useCallback(
      (templateId: string, templateName: string) => {
        onUseTemplate(templateId, templateName);
      },
      [onUseTemplate],
    );

    const handleEditTemplate = useCallback(() => {
      if (selectedTemplate && selectedTemplate.type === "custom") {
        setCustomTemplateModalState({
          isOpen: true,
          templateID: selectedTemplate.id,
        });
        setSelectedTemplate(null);
      }
    }, [selectedTemplate]);

    const handleBackToGrid = useCallback(() => {
      setSelectedTemplate(null);
    }, []);

    const handleFilterToggle = useCallback((filter: TemplateFilterType) => {
      setSelectedFilters((prev) =>
        prev.includes(filter)
          ? prev.filter((f) => f !== filter)
          : [...prev, filter],
      );
    }, []);

    const handleCreateTemplate = useCallback(() => {
      setCustomTemplateModalState({ isOpen: true });
      logComposeEvent(PAP_Click_CreateTemplateButton());
      resetTemplateState();
    }, [resetTemplateState, logComposeEvent]);

    const handleCloseTemplateModal = useCallback(() => {
      setCustomTemplateModalState({ isOpen: false });
    }, []);

    const handleDoneTemplateModal = useCallback(async () => {
      await handleClickSave();
      // The modal will be closed by the onDone callback after save completes
    }, [handleClickSave]);

    const handleDeleteTemplate = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setIsDeleteModalOpen(true);
    }, []);

    const [previousSources, setPreviousSources] = useState<
      typeof currentTemplateState.sources
    >([]);

    const onMount = useCallback(() => {
      const sourcesChanged = !isEqual(
        previousSources,
        currentTemplateState.sources,
      );
      const isNewTemplate = currentTemplateState.messagesHistory?.length === 0;
      if ((sourcesChanged && !!previousSources.length) || isNewTemplate) {
        handleModifyTemplatePostUserMessage("", { clearTemplate: true });
      }
      setPreviousSources(currentTemplateState.sources);
    }, [
      previousSources,
      currentTemplateState.sources,
      currentTemplateState.messagesHistory,
      handleModifyTemplatePostUserMessage,
    ]);

    const handleOnChangeTemplateContent = useCallback(
      (content: string) => {
        setCurrentTemplateState((prev) => ({
          ...prev,
          templateContent: content,
        }));
      },
      [setCurrentTemplateState],
    );

    useEffect(() => {
      if (customTemplateModalState.templateID) {
        const templateToEdit = templates.find(
          (template: AssistTemplate) =>
            template.id === customTemplateModalState.templateID,
        );
        if (
          templateToEdit &&
          "type" in templateToEdit &&
          templateToEdit.type === "custom"
        ) {
          setCurrentTemplateState(templateToEdit);
        }
      }
    }, [
      customTemplateModalState.templateID,
      templates,
      setCurrentTemplateState,
    ]);
    if (selectedTemplate) {
      return (
        <TemplateDetailView
          template={selectedTemplate}
          onBack={handleBackToGrid}
          onUseTemplate={handleUseTemplate}
          onEditTemplate={handleEditTemplate}
        />
      );
    }

    return (
      <div className={styles.TemplatesTab}>
        <div className={styles.TemplatesTabHeader}>
          <TextInput
            type="text"
            size="large"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            placeholder={i18n.t("ai_tool_library_search_templates")}
            className={styles.TemplatesTabSearch}
            withLeftAccessory={<UIIcon src={SearchLine} size="small" />}
          />
          <div className={styles.TemplatesTabFilters}>
            <div className={styles.TemplatesTabFilterChips}>
              <Chip
                variant="standard"
                size="medium"
                isSelected={selectedFilters.includes(TEMPLATE_FILTERS.YOUR)}
                onClick={() => handleFilterToggle(TEMPLATE_FILTERS.YOUR)}
              >
                <Chip.IconAccessory>
                  <UIIcon src={LockLine} size="small" />
                </Chip.IconAccessory>
                <Chip.Content>
                  {i18n.t("ai_tool_library_your_templates")}
                </Chip.Content>
              </Chip>
              <Chip
                variant="standard"
                size="medium"
                isSelected={selectedFilters.includes(TEMPLATE_FILTERS.COMPANY)}
                onClick={() => handleFilterToggle(TEMPLATE_FILTERS.COMPANY)}
              >
                <Chip.IconAccessory>
                  <UIIcon src={TeamLine} size="small" />
                </Chip.IconAccessory>
                <Chip.Content>
                  {i18n.t("ai_tool_library_company_templates")}
                </Chip.Content>
              </Chip>
              {showTemplateTypesFilter && (
                <Chip
                  variant="standard"
                  size="medium"
                  isSelected={selectedFilters.includes(TEMPLATE_FILTERS.TYPES)}
                  onClick={() => handleFilterToggle(TEMPLATE_FILTERS.TYPES)}
                >
                  <Chip.IconAccessory>
                    <UIIcon src={FileTemplateLine} size="small" />
                  </Chip.IconAccessory>
                  <Chip.Content>
                    {i18n.t("ai_tool_library_template_types")}
                  </Chip.Content>
                </Chip>
              )}
            </div>
            <Button
              variant="primary"
              size="medium"
              onClick={handleCreateTemplate}
              className={styles.TemplatesTabCreateButton}
              withIconStart={<UIIcon src={AddLine} size="small" />}
            >
              {i18n.t("ai_tool_library_create_template")}
            </Button>
          </div>
        </div>
        <div className={styles.TemplatesTabGrid}>
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onClick={() => handleTemplateClick(template)}
              isSelected={currentTemplateID === template.id}
            />
          ))}
        </div>
        {customTemplateModalState.isOpen && (
          <SourcesFromSettingsModalStepper
            steps={[
              <SourcesFromSettingsOne
                key={0}
                description={i18n.t("assist_template_sources_description")}
                sources={currentTemplateState?.sources}
                handleAddSource={handleAddSource}
                handleRemoveSource={handleRemoveSource}
                sourcesContents={sourcesContents}
                logComposeEvent={logComposeEvent}
              />,
              <SourcesFromSettingsTwo
                key={1}
                loading={loading}
                onMount={onMount}
                isTemplatePreview
                leftPaneContent={
                  <SourcesFromSettingsConversationPane
                    messages={currentTemplateState.messagesHistory}
                    logComposeEvent={logComposeEvent}
                    sources={currentTemplateState.sources}
                    onSubmitMessage={handleModifyTemplatePostUserMessage}
                    loading={loading}
                    loadingText={i18n.t("assist_template_loading_message")}
                    placeholder={i18n.t(
                      "compose_prompt_dash_placeholder_minimal_template",
                    )}
                  />
                }
                rightPaneContent={
                  <SourcesFromSettingsTemplatePreview
                    backgroundColor={getBackgroundForTemplate(
                      currentTemplateState,
                    )}
                    templateContent={currentTemplateState.templateContent}
                    onChangeTemplateContent={handleOnChangeTemplateContent}
                    loading={loading}
                  />
                }
              />,
              <SourcesFromSettingsThree
                key={2}
                title={i18n.t("assist_template_modal_stepper_save_title")}
                namePlaceholder={i18n.t(
                  "assist_templates_modal_stepper_name_placeholder",
                )}
                descriptionPlaceholder={i18n.t(
                  "assist_templates_modal_stepper_description_placeholder",
                )}
                onNameInputChange={handleChangeTemplateNameInput}
                onDescriptionInputChange={handleChangeTemplateDescriptionInput}
                nameValue={currentTemplateState.name}
                descriptionValue={currentTemplateState.description}
                deleteModal={
                  <DeleteConfirmationModal
                    title={i18n.t("assist_delete_templates_confirm_title")}
                    description={i18n.t(
                      "assist_delete_templates_confirm_description",
                    )}
                    onCancel={() => setIsDeleteModalOpen(false)}
                    onSubmit={() => {
                      if (onDeleteTemplate) {
                        onDeleteTemplate(currentTemplateState.id);
                      }
                    }}
                    isOpen={isDeleteModalOpen}
                  />
                }
                sharingSettings={
                  <SourcesFromSettingsSharingToggle
                    sublabelText={i18n.t(
                      "assist_templates_settings_from_sources_sublabel",
                    )}
                    accessType={currentTemplateState.accessType}
                    onAccessTypeChange={handleAccessTypeChange}
                  />
                }
              />,
            ]}
            widths={[720, 1150, 720]}
            isOpen={customTemplateModalState.isOpen}
            onRequestClose={handleCloseTemplateModal}
            onDone={handleDoneTemplateModal}
            onDelete={handleDeleteTemplate}
            initialStepIndex={customTemplateModalState.templateID ? 2 : 0}
          />
        )}
      </div>
    );
  },
);
TemplatesTab.displayName = "TemplatesTab";
